import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../components/PageConfigProvider/constants';
import type { TabContent } from '../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../components/PageConfigProvider/types';
import { FilterId } from '../filters/types/filters';

const geographyTransitionsInflow: TabContent['items'] = [
  {
    position: { x: 0, width: 32, y: 0, height: 36 },
    id: 'geography-transitions-inflow',
    title: 'Transition',
    info: {
      text: 'The most common sources of incoming workers and the most common destinations of outgoing workers.',
    },
    plot: {
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'sankey',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 6 },
    id: 'geography-transitions-inflow-role',
    title: 'Role',
    info: {
      text: 'The composition of workers by occupation. More information on the job representation and taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      secondaryDimension: 'role',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 6, height: 6 },
    id: 'geography-transitions-inflow-industry',
    title: 'Industry',
    info: {
      text: 'The composition of workers by industry.',
    },
    plot: {
      secondaryDimension: 'rics',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 12, height: 6 },
    id: 'geography-transitions-inflow-seniority',
    title: 'Seniority',
    info: {
      text: 'The composition of workers by seniority. More information on the seniority model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#seniority',
        },
      ],
    },
    plot: {
      secondaryDimension: 'seniority',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 6 },
    id: 'geography-transitions-inflow-gender',
    title: 'Gender',
    info: {
      text: 'The composition of workers by gender, predicted using first name. More information on the gender model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'gender',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 24, height: 6 },
    id: 'geography-transitions-inflow-ethnicity',
    title: 'Ethnicity',
    info: {
      text: 'The composition of workers by ethnicity, predicted using first name, last name, and geography. More information on the ethnicity model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'ethnicity',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 30, height: 6 },
    id: 'geography-transitions-inflow-education',
    title: 'Education',
    info: {
      text: 'The composition of workers by the highest educational degree.',
    },
    plot: {
      secondaryDimension: 'education',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
];
const geographyTransitionsOutflow: TabContent['items'] = [
  {
    position: { x: 0, width: 32, y: 0, height: 36 },
    id: 'geography-transitions-outflow',
    title: 'Transition',
    info: {
      text: 'The most common sources of incoming workers and the most common destinations of outgoing workers.',
    },
    plot: {
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'sankey',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 6 },
    id: 'geography-transitions-outflow-role',
    title: 'Role',
    info: {
      text: 'The composition of workers by occupation. More information on the job representation and taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      secondaryDimension: 'role',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 6, height: 6 },
    id: 'geography-transitions-outflow-industry',
    title: 'Industry',
    info: {
      text: 'The composition of workers by industry.',
    },
    plot: {
      secondaryDimension: 'rics',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 12, height: 6 },
    id: 'geography-transitions-outflow-seniority',
    title: 'Seniority',
    info: {
      text: 'The composition of workers by seniority. More information on the seniority model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#seniority',
        },
      ],
    },
    plot: {
      secondaryDimension: 'seniority',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 6 },
    id: 'geography-transitions-outflow-gender',
    title: 'Gender',
    info: {
      text: 'The composition of workers by gender, predicted using first name. More information on the gender model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'gender',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 24, height: 6 },
    id: 'geography-transitions-outflow-ethnicity',
    title: 'Ethnicity',
    info: {
      text: 'The composition of workers by ethnicity, predicted using first name, last name, and geography. More information on the ethnicity model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'ethnicity',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 30, height: 6 },
    id: 'geography-transitions-outflow-education',
    title: 'Education',
    info: {
      text: 'The composition of workers by the highest educational degree.',
    },
    plot: {
      secondaryDimension: 'education',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
];

const sharedConfig: Pick<
  TabContent,
  'grid' | 'primaryFilter' | 'secondaryFilters'
> = {
  primaryFilter: {
    id: FilterId.GEOGRAPHY,
    selectionLimit: 1,
    selectionDefault: 1,
  },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  secondaryFilters: [
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.SKILL,
    FilterId.CUSTOM_SKILL,
    FilterId.COMPANY,
    FilterId.DATE_RANGE,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
};

const geographyTransitionsInflowTab: TabContent = {
  id: 'geography-transitions-inflow',
  items: geographyTransitionsInflow,
  ...sharedConfig,
};

const geographyTransitionsOutflowTab: TabContent = {
  id: 'geography-transitions-outflow',
  items: geographyTransitionsOutflow,
  ...sharedConfig,
};

export const geographyTransitions: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.GEOGRAPHY_TRANSITIONS,
  revelioPageId: RevelioPageId.GEOGRAPHY_TRANSITIONS,
  name: 'Transitions',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'geography-transitions-inflow',
      label: 'Inflow',
      tabContent: geographyTransitionsInflowTab,
      isActive: true,
    },
    {
      id: 'geography-transitions-outflow',
      label: 'Outflow',
      tabContent: geographyTransitionsOutflowTab,
      isActive: false,
    },
  ],
};
